import { useSupportAccounts } from '@my/directory-api/hooks';
import type { SupportAccount } from '@my/directory-api';
import { useAccount } from '@my/platform/account';
import { useSupportSession } from '@my/platform/support';

export interface CurrentAccount {
  account: SupportAccount | undefined;
  accountId: string;
  isLoading: boolean;
  /**
   * Whether `X-Account-Key` is actually on the wire for the account below.
   * Anything reading or writing account-scoped data must wait for it, or the
   * request goes out scoped to no account and comes back empty.
   */
  isReady: boolean;
}

/**
 * The account every section of My Hub describes.
 *
 * <p>
 * TWO SOURCES, AND THE ORDER IS THE POINT. A Stratum support session, set from
 * the strip at the bottom, wins — that is somebody deliberately working on
 * another company. Otherwise it is the caller's own account, the way every
 * other app in the suite resolves it.
 * </p>
 *
 * <p>
 * So the day this opens up to account owners, the support branch simply never
 * fires for them and the sections carry on reading their own account. Nothing
 * in the sections has to know which branch supplied it.
 * </p>
 *
 * <p>
 * The account's DETAIL still comes from the staff-only support endpoint today.
 * That is the piece to swap when owners arrive — <c>/accounts/available</c>
 * already returns their own organisations and app roles — and it is why this
 * hook exists rather than each section fetching for itself.
 * </p>
 */
export function useCurrentAccount(): CurrentAccount {
  const { session } = useSupportSession();
  const { currentAccountId } = useAccount();
  const { data, isLoading } = useSupportAccounts();

  const accountId = session?.accountId || currentAccountId || '';
  const account = accountId ? data?.find((a) => a.id === accountId) : undefined;

  return {
    account,
    accountId,
    isLoading,
    // The header only travels for a support session; an owner reading their own
    // account needs no header at all, so they are ready as soon as it resolves.
    isReady: Boolean(account) && (!session || session.accountId === account?.id),
  };
}
